import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  PLATFORM_ID,
} from '@angular/core';
import { ErrorReportContext } from '@imagise/entities';
import { ErrorReportPrompter } from '../../error-reporting.config';
import { ErrorReportOverlayComponent } from './error-report-overlay.component';

/**
 * Imperatively shows the {@link ErrorReportOverlayComponent} for non-fatal
 * errors by creating it and attaching it to `document.body` — no Material/CDK.
 * Implements {@link ErrorReportPrompter}, so it can be wired to the reporting
 * pipeline as the non-fatal prompter. Browser-only; a no-op during SSR.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportOverlayService implements ErrorReportPrompter {
  readonly #appRef = inject(ApplicationRef);
  readonly #environmentInjector = inject(EnvironmentInjector);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #document = inject(DOCUMENT);

  #current: ComponentRef<ErrorReportOverlayComponent> | null = null;
  #previousFocus: HTMLElement | null = null;

  /** {@link ErrorReportPrompter} entry point. */
  prompt(context: ErrorReportContext): void {
    this.open(context);
  }

  /** Opens one overlay, keeping the current incident during an active crash loop. */
  open(context: ErrorReportContext): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (this.#current) return;
    this.#previousFocus = this.#document.activeElement as HTMLElement | null;

    const ref = createComponent(ErrorReportOverlayComponent, {
      environmentInjector: this.#environmentInjector,
    });
    ref.setInput('context', context);
    ref.instance.closed.subscribe(() => this.close());

    this.#appRef.attachView(ref.hostView);
    this.#document.body.appendChild(ref.location.nativeElement);
    this.#current = ref;
  }

  /** Closes and destroys the open overlay, if any. */
  close(): void {
    if (!this.#current) {
      return;
    }

    this.#appRef.detachView(this.#current.hostView);
    this.#current.destroy();
    this.#current = null;
    if (this.#previousFocus?.isConnected) this.#previousFocus.focus();
    this.#previousFocus = null;
  }
}
